import { useLang, LANGS } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

export const Flag = ({ code, className = "" }) => {
  const base = `relative h-4 w-6 overflow-hidden rounded-[2px] ring-1 ring-white/10 ${className}`;
  if (code === "uz")
    return (
      <span className={base} aria-hidden>
        <span className="absolute inset-x-0 top-0 h-1/3 bg-[#1EB5E8]" />
        <span className="absolute inset-x-0 top-1/3 h-1/3 bg-white" />
        <span className="absolute inset-x-0 bottom-0 h-1/3 bg-[#1EB53A]" />
      </span>
    );
  if (code === "tg")
    return (
      <span className={base} aria-hidden>
        <span className="absolute inset-x-0 top-0 h-[28%] bg-[#CC0000]" />
        <span className="absolute inset-x-0 top-[28%] h-[44%] bg-white" />
        <span className="absolute inset-x-0 bottom-0 h-[28%] bg-[#006600]" />
        <span className="absolute left-1/2 top-1/2 h-[5px] w-[5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#F8C300]" />
      </span>
    );
  if (code === "ru")
    return (
      <span className={base} aria-hidden>
        <span className="absolute inset-x-0 top-0 h-1/3 bg-white" />
        <span className="absolute inset-x-0 top-1/3 h-1/3 bg-[#0039A6]" />
        <span className="absolute inset-x-0 bottom-0 h-1/3 bg-[#D52B1E]" />
      </span>
    );
  return (
    <span className={`${base} bg-[#012169]`} aria-hidden>
      <span className="absolute left-1/2 top-1/2 h-[2px] w-[150%] -translate-x-1/2 -translate-y-1/2 rotate-[33deg] bg-white" />
      <span className="absolute left-1/2 top-1/2 h-[2px] w-[150%] -translate-x-1/2 -translate-y-1/2 -rotate-[33deg] bg-white" />
      <span className="absolute left-1/2 top-1/2 h-[1px] w-[150%] -translate-x-1/2 -translate-y-1/2 rotate-[33deg] bg-[#C8102E]" />
      <span className="absolute left-1/2 top-1/2 h-[1px] w-[150%] -translate-x-1/2 -translate-y-1/2 -rotate-[33deg] bg-[#C8102E]" />
      <span className="absolute left-1/2 top-0 h-full w-[4px] -translate-x-1/2 bg-white" />
      <span className="absolute top-1/2 left-0 h-[4px] w-full -translate-y-1/2 bg-white" />
      <span className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-[#C8102E]" />
      <span className="absolute top-1/2 left-0 h-[2px] w-full -translate-y-1/2 bg-[#C8102E]" />
    </span>
  );
};

export function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center gap-1 rounded-full border border-vox-line bg-vox-deep/60 p-1" data-testid="language-switcher">
      {LANGS.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          data-testid={`lang-option-${code}`}
          className={`flex items-center gap-1.5 rounded-full py-1 font-mono text-[11px] uppercase tracking-wider transition-colors duration-200 ${compact ? "px-1.5 sm:px-2.5" : "px-2.5"} ${
            lang === code ? "bg-vox-card text-vox-text" : "text-vox-dim hover:text-vox-text"
          }`}
        >
          <Flag code={code} />
          {!compact && code}
        </button>
      ))}
    </div>
  );
}

export function LangGate() {
  const { chosen, setLang } = useLang();
  const { config } = useAuth();
  if (chosen) return null;
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-vox-deep" data-testid="language-gate">
      <div className="flex flex-col items-center gap-10 reveal">
        <div className="flex items-center gap-4">
          <span className="relative grid h-12 w-12 place-items-center">
            <span className="absolute h-3 w-3 rounded-full bg-vox-bright animate-breathe" />
            <span className="h-12 w-12 rounded-full border border-vox-accent/40" />
          </span>
          <span className="font-sans text-3xl font-bold tracking-tight text-vox-text">{config.brand}</span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 px-4">
          {LANGS.map((code, i) => (
            <button
              key={code}
              type="button"
              onClick={() => setLang(code)}
              data-testid={`gate-lang-${code}`}
              className={`reveal reveal-${i + 1} group flex h-16 w-20 flex-col items-center justify-center gap-2 rounded-xl border border-vox-line bg-vox-panel transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-vox-bright sm:w-24`}
            >
              <Flag code={code} className="h-5 w-8" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-vox-dim group-hover:text-vox-text">{code}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
